import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Card,
  Button,
  Form,
  Select,
  DatePicker,
  Input,
  Modal,
  Table,
  Tag,
  Space,
  Typography,
  Progress,
  Alert,
  Descriptions,
  message,
  Tooltip,
  Popconfirm,
} from 'antd';
import {
  PlusOutlined,
  ReloadOutlined,
  StopOutlined,
  EyeOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  ClockCircleOutlined,
  LoadingOutlined,
  MinusCircleOutlined,
  SearchOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import axios from '../../../utils/axios';
import { useAuthCheck } from '../../../hooks/useAuth';

dayjs.extend(relativeTime);

const { Title, Text } = Typography;
const { RangePicker } = DatePicker;

const TERMINAL_STATUSES = ['completed', 'failed', 'cancelled'];

const isJobRunning = (status) => !TERMINAL_STATUSES.includes(status);

const getStatusColor = (status) => {
  const colors = {
    pending: 'orange',
    counting: 'blue',
    cleaning_insights: 'blue',
    reprocessing: 'blue',
    completed: 'green',
    failed: 'red',
    cancelled: 'default',
  };
  return colors[status] || 'default';
};

const getStatusIcon = (status) => {
  const style = { fontSize: '14px', marginRight: '6px' };
  const icons = {
    pending: <ClockCircleOutlined style={{ ...style, color: '#faad14' }} />,
    counting: <LoadingOutlined spin style={{ ...style, color: '#1890ff' }} />,
    cleaning_insights: <LoadingOutlined spin style={{ ...style, color: '#1890ff' }} />,
    reprocessing: <LoadingOutlined spin style={{ ...style, color: '#1890ff' }} />,
    completed: <CheckCircleOutlined style={{ ...style, color: '#52c41a' }} />,
    failed: <CloseCircleOutlined style={{ ...style, color: '#ff4d4f' }} />,
    cancelled: <MinusCircleOutlined style={{ ...style, color: '#8c8c8c' }} />,
  };
  return icons[status] || <ClockCircleOutlined style={style} />;
};

const formatStatus = (status) =>
  status
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase());

const STALE_THRESHOLD_MS = 2 * 60 * 1000;

const isStale = (job) => {
  if (!isJobRunning(job.status) || !job.updated_at) return false;
  return Date.now() - dayjs(job.updated_at).valueOf() > STALE_THRESHOLD_MS;
};

const formatEta = (etaSeconds) => {
  if (etaSeconds === null || etaSeconds === undefined) return 'ETA unknown';
  if (etaSeconds < 60) return `ETA ${Math.round(etaSeconds)}s`;
  if (etaSeconds < 3600) return `ETA ${Math.round(etaSeconds / 60)}m`;
  return `ETA ${(etaSeconds / 3600).toFixed(1)}h`;
};

const formatDuration = (seconds) => {
  if (!seconds) return '0s';
  if (seconds < 60) return `${Math.round(seconds)}s`;
  if (seconds < 3600) return `${Math.floor(seconds / 60)}m ${Math.round(seconds % 60)}s`;
  return `${Math.floor(seconds / 3600)}h ${Math.round((seconds % 3600) / 60)}m`;
};

const ReprocessingPage = () => {
  useAuthCheck();

  const [isAdmin, setIsAdmin] = useState(false);
  const [jobs, setJobs] = useState([]);
  const [jobsLoading, setJobsLoading] = useState(false);
  const [processors, setProcessors] = useState([]);
  const [tenants, setTenants] = useState([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [previewResult, setPreviewResult] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [detailJobId, setDetailJobId] = useState(null);
  const [form] = Form.useForm();
  const pollingRef = useRef(null);

  // Auth check
  useEffect(() => {
    const checkAdmin = async () => {
      try {
        const res = await axios.get('/iam/me');
        const d = res.data;
        setIsAdmin(d.role === 'admin' && d.tenant_code === 'admin');
      } catch {
        setIsAdmin(false);
      }
    };
    checkAdmin();
  }, []);

  // Load initial data
  const loadJobs = useCallback(async () => {
    setJobsLoading(true);
    try {
      const res = await axios.get('/admin/reprocessing/jobs?limit=50');
      setJobs(res.data);
    } catch (err) {
      console.error('Failed to load jobs:', err);
    } finally {
      setJobsLoading(false);
    }
  }, []);

  const loadProcessors = useCallback(async () => {
    try {
      const res = await axios.get('/admin/reprocessing/processors');
      setProcessors(res.data);
    } catch (err) {
      console.error('Failed to load processors:', err);
    }
  }, []);

  const loadTenants = useCallback(async () => {
    try {
      const res = await axios.get('/admin/tenants');
      setTenants(res.data);
    } catch (err) {
      console.error('Failed to load tenants:', err);
    }
  }, []);

  useEffect(() => {
    if (isAdmin) {
      loadJobs();
      loadProcessors();
      loadTenants();
    }
  }, [isAdmin, loadJobs, loadProcessors, loadTenants]);

  // Polling
  useEffect(() => {
    const hasRunning = jobs.some((j) => isJobRunning(j.status));
    if (hasRunning && !pollingRef.current) {
      pollingRef.current = setInterval(() => loadJobs(), 5000);
    } else if (!hasRunning && pollingRef.current) {
      clearInterval(pollingRef.current);
      pollingRef.current = null;
    }
    return () => {
      if (pollingRef.current) clearInterval(pollingRef.current);
    };
  }, [jobs, loadJobs]);

  // Handlers
  const buildRequestBody = (values) => ({
    tenant_id: values.tenant_id,
    processor_names: values.processor_names,
    start_date: values.date_range[0].toISOString(),
    end_date: values.date_range[1].toISOString(),
    type_id: values.type_id || undefined,
    event_type: values.event_type || undefined,
    version: values.version || undefined,
  });

  const handlePreview = async () => {
    try {
      const values = await form.validateFields();
      setPreviewLoading(true);
      setPreviewResult(null);
      const res = await axios.post('/admin/reprocessing/preview', buildRequestBody(values));
      setPreviewResult(res.data);
    } catch (err) {
      if (err.errorFields) return; // validation error
      message.error('Preview failed');
      console.error(err);
    } finally {
      setPreviewLoading(false);
    }
  };

  const handleSubmit = async (values) => {
    setSubmitting(true);
    try {
      await axios.post('/admin/reprocessing/jobs', buildRequestBody(values));
      message.success('Reprocessing job created');
      setModalOpen(false);
      setPreviewResult(null);
      form.resetFields();
      loadJobs();
    } catch (err) {
      const detail = err.response?.data?.detail;
      message.error(detail || 'Failed to create job');
    } finally {
      setSubmitting(false);
    }
  };

  const handleCancel = async (jobId) => {
    try {
      await axios.post(`/admin/reprocessing/jobs/${jobId}/cancel`);
      message.success('Job cancelled');
      loadJobs();
    } catch (err) {
      message.error('Failed to cancel job');
    }
  };

  // Table columns
  const columns = [
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: 180,
      render: (status, record) => (
        <div>
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <Tooltip title={formatStatus(status)}>
              {getStatusIcon(status)}
            </Tooltip>
            <Tag color={getStatusColor(status)}>{formatStatus(status)}</Tag>
            {isStale(record) && (
              <Tooltip title={`No progress update since ${dayjs(record.updated_at).format('HH:mm:ss')}`}>
                <Tag color="orange">No updates for {dayjs().diff(dayjs(record.updated_at), 'minute')}m</Tag>
              </Tooltip>
            )}
          </div>
          {isJobRunning(status) && (
            <div style={{ fontSize: '12px', color: '#666', marginTop: 4, marginLeft: 20 }}>
              {Math.round(record.progress_percentage)}% &mdash; {record.current_step || 'Initializing...'}
            </div>
          )}
          {status === 'failed' && record.error_message && (
            <div style={{ fontSize: '11px', color: '#ff4d4f', marginTop: 4, marginLeft: 20 }} title={record.error_message}>
              {record.error_message.length > 60 ? record.error_message.substring(0, 60) + '...' : record.error_message}
            </div>
          )}
        </div>
      ),
    },
    {
      title: 'Processors',
      dataIndex: 'parameters',
      key: 'processors',
      render: (params) => (
        <Space wrap size={[0, 4]}>
          {params.processor_names?.map((p) => (
            <Tag key={p}>{p}</Tag>
          ))}
        </Space>
      ),
    },
    {
      title: 'Date Range',
      dataIndex: 'parameters',
      key: 'date_range',
      render: (params) =>
        `${dayjs(params.start_date).format('YYYY-MM-DD')} - ${dayjs(params.end_date).format('YYYY-MM-DD')}`,
    },
    {
      title: 'Progress',
      key: 'progress',
      width: 120,
      render: (_, record) => (
        <Progress
          percent={Math.round(record.progress_percentage)}
          size="small"
          status={record.status === 'failed' ? 'exception' : record.status === 'completed' ? 'success' : 'active'}
        />
      ),
    },
    {
      title: 'Created',
      dataIndex: 'created_at',
      key: 'created_at',
      width: 140,
      render: (d) => dayjs(d).format('YYYY-MM-DD HH:mm'),
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 100,
      render: (_, record) => (
        <Space size="small">
          <Tooltip title="View details">
            <Button icon={<EyeOutlined />} size="small" onClick={() => setDetailJobId(record.id)} />
          </Tooltip>
          {isJobRunning(record.status) && (
            <Popconfirm title="Cancel this job?" onConfirm={() => handleCancel(record.id)} okText="Yes" cancelText="No">
              <Tooltip title="Cancel">
                <Button icon={<StopOutlined />} size="small" danger />
              </Tooltip>
            </Popconfirm>
          )}
        </Space>
      ),
    },
  ];

  const detailJob = jobs.find((j) => j.id === detailJobId) || null;

  if (!isAdmin) {
    return <Alert type="error" message="Super admin access required" showIcon />;
  }

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <Title level={3} style={{ margin: 0 }}>Event Reprocessing</Title>
        <Space>
          <Button icon={<ReloadOutlined />} onClick={loadJobs} loading={jobsLoading}>
            Refresh
          </Button>
          <Button type="primary" icon={<PlusOutlined />} onClick={() => { setModalOpen(true); setPreviewResult(null); }}>
            New Reprocessing Job
          </Button>
        </Space>
      </div>

      <Card>
        <Table
          columns={columns}
          dataSource={jobs}
          rowKey="id"
          loading={jobsLoading}
          pagination={{ pageSize: 20 }}
          locale={{ emptyText: 'No reprocessing jobs yet' }}
        />
      </Card>

      {/* Create Job Modal */}
      <Modal
        title="Create Reprocessing Job"
        open={modalOpen}
        onCancel={() => { setModalOpen(false); setPreviewResult(null); form.resetFields(); }}
        footer={null}
        width={640}
        destroyOnClose
      >
        <Form form={form} layout="vertical" onFinish={handleSubmit}>
          <Form.Item name="tenant_id" label="Tenant" rules={[{ required: true, message: 'Select a tenant' }]}>
            <Select
              placeholder="Select tenant"
              showSearch
              optionFilterProp="label"
              options={tenants.map((t) => ({ value: t._id || t.id, label: `${t.name} (${t.tenant_code})` }))}
            />
          </Form.Item>

          <Form.Item name="processor_names" label="Processors" rules={[{ required: true, message: 'Select at least one processor' }]}>
            <Select
              mode="multiple"
              placeholder="Select processors"
              options={processors.map((p) => ({
                value: p.key,
                label: `${p.key} (${p.version})`,
              }))}
            />
          </Form.Item>

          <Form.Item name="date_range" label="Date Range" rules={[{ required: true, message: 'Select a date range' }]}>
            <RangePicker showTime style={{ width: '100%' }} />
          </Form.Item>

          <Form.Item name="type_id" label="Type ID (optional)">
            <Input placeholder="e.g. hubspot_sequence_email" />
          </Form.Item>

          <Form.Item name="event_type" label="Event Type (optional)">
            <Input placeholder="e.g. customer.subscription.created" />
          </Form.Item>

          <Form.Item name="version" label="Processor Version (optional)">
            <Input placeholder="e.g. short" />
          </Form.Item>

          {/* Preview section */}
          <div style={{ marginBottom: 16 }}>
            <Button icon={<SearchOutlined />} onClick={handlePreview} loading={previewLoading}>
              Preview
            </Button>
            {previewResult && (
              <Card size="small" style={{ marginTop: 12 }}>
                <Descriptions column={2} size="small">
                  <Descriptions.Item label="Events">{previewResult.events_count.toLocaleString()}</Descriptions.Item>
                  <Descriptions.Item label="Insights">{previewResult.insights_count.toLocaleString()}</Descriptions.Item>
                  <Descriptions.Item label="Gold Insights">{previewResult.gold_insights_count.toLocaleString()}</Descriptions.Item>
                  <Descriptions.Item label="Insight Types">{previewResult.insight_types?.join(', ')}</Descriptions.Item>
                </Descriptions>
                <div style={{ marginTop: 8, fontSize: '12px', color: '#666' }}>
                  Collections: {previewResult.collections_affected?.join(', ')}
                </div>
              </Card>
            )}
          </div>

          <Form.Item style={{ textAlign: 'right', marginBottom: 0 }}>
            <Space>
              <Button onClick={() => { setModalOpen(false); setPreviewResult(null); form.resetFields(); }}>
                Cancel
              </Button>
              <Button type="primary" htmlType="submit" loading={submitting}>
                Create Job
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      {/* Job Detail Modal */}
      <Modal
        title="Job Details"
        open={!!detailJobId}
        onCancel={() => setDetailJobId(null)}
        footer={<Button onClick={() => setDetailJobId(null)}>Close</Button>}
        width={640}
      >
        {detailJob && (
          <div>
            <Descriptions column={2} bordered size="small">
              <Descriptions.Item label="ID">{detailJob.id}</Descriptions.Item>
              <Descriptions.Item label="Status">
                <Tag color={getStatusColor(detailJob.status)}>{formatStatus(detailJob.status)}</Tag>
              </Descriptions.Item>
              <Descriptions.Item label="Created By">{detailJob.created_by}</Descriptions.Item>
              <Descriptions.Item label="Created">{dayjs(detailJob.created_at).format('YYYY-MM-DD HH:mm:ss')}</Descriptions.Item>
              {detailJob.updated_at && (
                <Descriptions.Item label="Last Update" span={2}>
                  {dayjs(detailJob.updated_at).fromNow()} ({dayjs(detailJob.updated_at).format('HH:mm:ss')})
                  {isStale(detailJob) && (
                    <Tag color="orange" style={{ marginLeft: 8 }}>Stale &mdash; job may be stuck</Tag>
                  )}
                </Descriptions.Item>
              )}
              <Descriptions.Item label="Processors" span={2}>
                {detailJob.parameters?.processor_names?.join(', ')}
              </Descriptions.Item>
              <Descriptions.Item label="Date Range" span={2}>
                {dayjs(detailJob.parameters?.start_date).format('YYYY-MM-DD HH:mm')} &rarr; {dayjs(detailJob.parameters?.end_date).format('YYYY-MM-DD HH:mm')}
              </Descriptions.Item>
              {detailJob.parameters?.type_id && (
                <Descriptions.Item label="Type ID">{detailJob.parameters.type_id}</Descriptions.Item>
              )}
              {detailJob.parameters?.event_type && (
                <Descriptions.Item label="Event Type">{detailJob.parameters.event_type}</Descriptions.Item>
              )}
              <Descriptions.Item label="Progress" span={2}>
                <Progress
                  percent={Math.round(detailJob.progress_percentage)}
                  status={detailJob.status === 'failed' ? 'exception' : detailJob.status === 'completed' ? 'success' : 'active'}
                />
              </Descriptions.Item>
              {detailJob.current_step && (
                <Descriptions.Item label="Current Step" span={2}>{detailJob.current_step}</Descriptions.Item>
              )}
              {detailJob.error_message && (
                <Descriptions.Item label="Error" span={2}>
                  <Text type="danger">{detailJob.error_message}</Text>
                </Descriptions.Item>
              )}
            </Descriptions>

            {isJobRunning(detailJob.status) && detailJob.result && (
              <Card size="small" title="Live Progress" style={{ marginTop: 16 }}>
                <Descriptions column={2} size="small">
                  <Descriptions.Item label="Scanned" span={2}>
                    {detailJob.result.events_scanned?.toLocaleString()} / {detailJob.result.events_matched?.toLocaleString()}
                  </Descriptions.Item>
                  <Descriptions.Item label="Reprocessed">{detailJob.result.events_reprocessed?.toLocaleString()}</Descriptions.Item>
                  <Descriptions.Item label="Skipped">{detailJob.result.events_skipped?.toLocaleString()}</Descriptions.Item>
                  <Descriptions.Item label="Failed">{detailJob.result.events_failed?.toLocaleString()}</Descriptions.Item>
                  <Descriptions.Item label="Insights Created">{detailJob.result.insights_created?.toLocaleString()}</Descriptions.Item>
                  <Descriptions.Item label="Throughput">{Math.round(detailJob.result.events_per_second || 0).toLocaleString()} ev/s</Descriptions.Item>
                  <Descriptions.Item label="ETA">{formatEta(detailJob.result.eta_seconds)}</Descriptions.Item>
                  <Descriptions.Item label="Elapsed">{formatDuration(detailJob.result.duration_seconds)}</Descriptions.Item>
                  {detailJob.cursor_timestamp && (
                    <Descriptions.Item label="Position">{dayjs(detailJob.cursor_timestamp).format('YYYY-MM-DD HH:mm:ss')}</Descriptions.Item>
                  )}
                </Descriptions>
                {detailJob.result.last_error && (
                  <div style={{ marginTop: 8 }}>
                    <Text type="danger" style={{ fontSize: '12px' }}>Last error: {detailJob.result.last_error}</Text>
                  </div>
                )}
              </Card>
            )}

            {detailJob.result && (
              <Card size="small" title="Results" style={{ marginTop: 16 }}>
                <Descriptions column={2} size="small">
                  <Descriptions.Item label="Events Matched">{detailJob.result.events_matched?.toLocaleString()}</Descriptions.Item>
                  <Descriptions.Item label="Events Reprocessed">{detailJob.result.events_reprocessed?.toLocaleString()}</Descriptions.Item>
                  <Descriptions.Item label="Events Skipped">{detailJob.result.events_skipped?.toLocaleString()}</Descriptions.Item>
                  <Descriptions.Item label="Events Failed">{detailJob.result.events_failed?.toLocaleString()}</Descriptions.Item>
                  <Descriptions.Item label="Insights Deleted">{detailJob.result.insights_deleted?.toLocaleString()}</Descriptions.Item>
                  <Descriptions.Item label="Gold Insights Deleted">{detailJob.result.gold_insights_deleted?.toLocaleString()}</Descriptions.Item>
                  <Descriptions.Item label="Insights Created">{detailJob.result.insights_created?.toLocaleString()}</Descriptions.Item>
                  {detailJob.result.duration_seconds > 0 && (
                    <Descriptions.Item label="Duration">{formatDuration(detailJob.result.duration_seconds)}</Descriptions.Item>
                  )}
                </Descriptions>
                {!isJobRunning(detailJob.status) && detailJob.result.last_error && (
                  <div style={{ marginTop: 8 }}>
                    <Text type="danger" style={{ fontSize: '12px' }}>Last error: {detailJob.result.last_error}</Text>
                  </div>
                )}
              </Card>
            )}
          </div>
        )}
      </Modal>
    </div>
  );
};

export default ReprocessingPage;
